import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ModalLayout from '../layouts/ModalLayout'
import ContactModal from './ContactModal'
import AdvertisementOrderModal from './AdvertisementOrderModal'
import AdvertisingTermsModal from './AdvertisingTermsModal'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const fallbackPackages = [
  {
    id: 1,
    name: 'MINI',
    price: 49,
    impressions: 5000,
    dynamic_multiplier: 1.3,
    video_multiplier: 1.7,
    is_active: true
  },
  {
    id: 2,
    name: 'START',
    price: 89,
    impressions: 10000,
    dynamic_multiplier: 1.3,
    video_multiplier: 1.7,
    is_active: true
  },
  {
    id: 3,
    name: 'PRO',
    price: 159,
    impressions: 25000,
    dynamic_multiplier: 1.3,
    video_multiplier: 1.7,
    is_active: true
  },
  {
    id: 4,
    name: 'MAX',
    price: 299,
    impressions: 50000,
    dynamic_multiplier: 1.3,
    video_multiplier: 1.7,
    is_active: true
  }
]

const cardStyle = {
  background: '#fff',
  border: '1px solid #1e3f44',
  borderRadius: 12,
  padding: 12,
  marginBottom: 8
}

export default function AdvertisementModal({ open, onClose }) {
  const { t, i18n } = useTranslation('advertisement')
  const [advertisementTypes, setAdvertisementTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [termsOpen, setTermsOpen] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [orderOpen, setOrderOpen] = useState(false)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)

  useEffect(() => {
    if (!open) return

    let mounted = true

    const loadAdvertisementTypes = async () => {
      setLoading(true)

      try {
        const res = await fetchWithAuth(
          '/advertisement/types',
          { cacheTtlMs: 5 * 60 * 1000 },
          i18n.language || 'sk'
        )

        const data = await res.json().catch(() => [])

        if (mounted) {
          setAdvertisementTypes(Array.isArray(data) ? data : [])
        }
      } catch (err) {
        console.error(err)

        if (mounted) {
          setAdvertisementTypes([])
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadAdvertisementTypes()

    return () => {
      mounted = false
    }
  }, [open, i18n.language])

  const packages = useMemo(() => {
    return (advertisementTypes.length
      ? advertisementTypes
      : fallbackPackages)
      .filter(pkg => pkg.is_active !== false)
      .sort((a, b) => Number(a.id) - Number(b.id))
  }, [advertisementTypes])

  const videoMultiplier = Number(packages[0]?.video_multiplier)

  const formatPrice = value =>
    Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })

  const formatImpressions = value => Number(value).toLocaleString()

  const formatSurcharge = multiplier =>
    Math.round((multiplier - 1) * 100)

  const openOrder = pkg => {
    setSelectedPackage(pkg)
    setOrderOpen(true)
  }

const handleClose = () => {
  setHoverBtn(null)
  setPressedBtn(null)
  onClose()
}

  if (!open) return null

  return createPortal(
    <>
<ModalLayout
  open={open}
  onClose={handleClose}
  size={window.innerWidth <= 768 ? 'fullscreen' : 'large'}
  zIndex={3000}
  closeOnEscape={!termsOpen && !contactOpen && !orderOpen}
>
        <h3
          style={{
            fontSize: 24,
            margin: 0,
            marginBottom: 8,
            textAlign: 'center'
          }}
        >
          {t('title')}
        </h3>

        <div
          style={{
            overflowY: 'auto',
            flex: 1,
            paddingRight: 4
          }}
        >
          {loading && !packages.length ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center'
              }}
            >
              {t('loading')}
            </div>
          ) : packages.length ? (
            packages.map(pkg => (
              <button
                key={pkg.id}
                type="button"
                onClick={() => openOrder(pkg)}
                style={{
                  ...cardStyle,
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#000'
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: '#1e3f44',
                    fontSize: 18
                  }}
                >
                  {pkg.name}
                </div>

                <div style={{ marginTop: 2 }}>
                  {t('priceLabel')}:{' '}
                  {formatPrice(pkg.price)} €
                </div>

                <div>
                  {t('impressionsLabel')}:{' '}
                  {formatImpressions(pkg.impressions)}
                </div>
              </button>
            ))
          ) : (
            <div
              style={{
                margin: '12px 16px',
                textAlign: 'center'
              }}
            >
              {t('empty')}
            </div>
          )}

          <div
            style={{
              height: 1,
              background: '#ccc',
              margin: '6px 0'
            }}
          />

          <div style={cardStyle}>
            <div
              style={{
                fontWeight: 700,
                marginBottom: 4
              }}
            >
              {t('typeTitle')}
            </div>

            <div>
              {t('types.static')}: {t('basePrice')}
            </div>

            {Number.isFinite(videoMultiplier) && (
              <div>
                {t('types.video')}:{' '}
                {t('typeSurcharge', {
                  value: formatSurcharge(videoMultiplier)
                })}
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              {t('disclaimer')}
            </div>

            <div
              style={{
                height: 1,
                background: '#ccc',
                margin: '12px 0 8px'
              }}
            />

            <div>{t('contactInfo')}</div>
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            marginTop: 8
          }}
        >
          <button
            style={modalPrimaryButtonStyle(
              pressedBtn,
              'contact',
              hoverBtn === 'contact'
            )}
            onMouseEnter={() => setHoverBtn('contact')}
            onMouseLeave={() => setHoverBtn(null)}
            onMouseDown={() => setPressedBtn('contact')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={() => setContactOpen(true)}
          >
            {t('contactButton')}
          </button>

          <button
            style={modalSecondaryButtonStyle(
              pressedBtn,
              'close',
              hoverBtn === 'close'
            )}
	onMouseEnter={() => setHoverBtn('close')}
	onMouseLeave={() => {
 	 setHoverBtn(null)
 	 setPressedBtn(null)
	}}
	onMouseDown={() => setPressedBtn('close')}
	onMouseUp={() => setPressedBtn(null)}
	onClick={handleClose}
          >
            {t('closeButton')}
          </button>

          <button
	style={{
	  ...modalPrimaryButtonStyle(
 	   pressedBtn,
 	   'terms',
  	  hoverBtn === 'terms'
 	 ),
 	 position: 'absolute',
 	 right: 0,
 	 bottom: 0,
  	width: 42,
  	height: 42,
 	 minWidth: 42,
 	 padding: 0,
 	 borderRadius: '50%',
 	 display: 'flex',
  	alignItems: 'center',
  	justifyContent: 'center'
	}}
            onMouseEnter={() => setHoverBtn('terms')}
            onMouseLeave={() => setHoverBtn(null)}
            onMouseDown={() => setPressedBtn('terms')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={() => setTermsOpen(true)}
          >
            <InfoOutlinedIcon fontSize="small" />
          </button>
        </div>
      </ModalLayout>

      <AdvertisementOrderModal
        open={orderOpen}
        advertisementType={selectedPackage}
        onClose={() => {
          setOrderOpen(false)
          setSelectedPackage(null)
        }}
      />

      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />

      <AdvertisingTermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
      />
    </>,
    document.body
  )
}